import { useEffect, useState } from "react";
import { History, X } from "lucide-react";
import { api } from "../../lib/api";
import { GateHeatmap } from "./GateHeatmap";

/* VETO DASHBOARD — every gate the last triggered signal passed / failed / skipped, in order. */
const TONE = { PASS: "var(--r2-emerald)", FAIL: "var(--r2-red)", SKIPPED: "var(--r2-text2)" };
const when = (ts) => new Date(ts * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
const day = (ts) => new Date(ts * 1000).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

const Outcome = ({ t, testid }) => (
  <span className="font-bold tracking-[0.14em]" style={{ color: t.sent ? "var(--r2-emerald)" : t.outcome === "AWAITING CONFIRMATION" ? "var(--r2-amber)" : "var(--r2-red)" }} data-testid={testid}>{t.outcome}</span>
);

const Chain = ({ t, compact, prefix }) => (
  <ol className={compact ? "flex flex-wrap gap-x-2 gap-y-[2px]" : "flex flex-col gap-[2px]"} data-testid={`${prefix}-chain`}>
    {t.gates.map((g, i) => (
      <li key={i} className="flex items-start gap-1" title={g.reason || g.note || g.gate} data-testid={`${prefix}-gate-${i}`}>
        <span className="mt-[3px] inline-block h-[6px] w-[6px] shrink-0 rounded-full" style={{ background: TONE[g.status], opacity: g.status === "SKIPPED" ? 0.45 : 1 }} />
        <span className={g.status === "FAIL" ? "font-bold text-[var(--r2-red)]" : g.status === "SKIPPED" ? "text-[var(--r2-text2)] opacity-70" : "text-[var(--r2-text1)]"}>
          {g.gate}{!compact && g.note ? <span className="text-[var(--r2-text2)]"> · {g.note}</span> : null}
          {g.status === "FAIL" && <span className="text-[var(--r2-red)]"> — {g.reason}</span>}
        </span>
      </li>
    ))}
  </ol>
);

const Head = ({ t, testid }) => (
  <div className="flex flex-wrap items-center gap-x-2 gap-y-[1px]" data-testid={testid}>
    <span style={{ color: t.direction === "LONG" ? "var(--r2-emerald)" : "var(--r2-red)" }} className="font-bold">{t.direction} {t.symbol}</span>
    <span className="text-[var(--r2-text2)]">{t.grade} · {t.score}/{t.threshold} · {when(t.last_seen || t.ts)}</span>
    <Outcome t={t} testid={`${testid}-outcome`} />
    <span className="text-[var(--r2-text2)]">{t.passed}/{t.total} gates{t.count > 1 ? ` · repeated ×${t.count}` : ""}</span>
  </div>
);

const HistoryModal = ({ rows, symbol, onSymbol, onClose }) => {
  const [open, setOpen] = useState(rows[0]?.id);
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="gates-history-modal">
      <div className="r2-panel w-[min(860px,94vw)] max-h-[86vh] overflow-auto p-4 text-[8.5px] font-mono !bg-[var(--r2-bg)]" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <div><div className="text-[10px] font-black tracking-[0.2em] text-[var(--r2-text1)]">GATE HISTORY · {symbol}</div><div className="text-[var(--r2-text2)]">last {rows.length} triggered signals — identical repeats collapsed</div></div>
          <button onClick={onClose} className="text-[var(--r2-text2)] hover:text-[var(--r2-text1)]" data-testid="gates-history-close"><X size={12} /></button>
        </div>
        <GateHeatmap symbol={symbol} onSymbol={onSymbol} />
        {rows.length === 0 && <div className="text-[var(--r2-text2)]">no triggered signals recorded yet</div>}
        {rows.map((t, i) => (
          <div key={t.id} className="border-t border-[var(--r2-border)] py-2" data-testid={`gates-history-row-${i}`}>
            <button className="w-full text-left" onClick={() => setOpen(open === t.id ? null : t.id)} data-testid={`gates-history-toggle-${i}`}>
              <div className="flex items-center justify-between gap-2"><Head t={t} testid={`gates-history-head-${i}`} /><span className="text-[var(--r2-text2)]">{day(t.ts)}</span></div>
            </button>
            {open === t.id && <div className="mt-1 pl-2"><Chain t={t} prefix={`gates-history-${i}`} /></div>}
          </div>
        ))}
      </div>
    </div>
  );
};

export const MbtGates = ({ symbol = "MBT" }) => {
  const [rows, setRows] = useState([]);
  const [hist, setHist] = useState(false);
  const [histSym, setHistSym] = useState(symbol);
  const [histRows, setHistRows] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.gateTraces(symbol).then((r) => alive && setRows(r.traces || [])).catch(() => {});
    load(); const id = setInterval(load, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [symbol]);
  useEffect(() => {
    if (!hist) return;
    if (histSym === symbol) { setHistRows(null); return; }
    let alive = true;
    api.gateTraces(histSym).then((r) => alive && setHistRows(r.traces || [])).catch(() => {});
    return () => { alive = false; };
  }, [hist, histSym, symbol]);
  const last = rows[0];
  return (
    <div className="border-t border-[var(--r2-border)] pt-1" data-testid="mbt2-gates">
      <div className="flex items-center justify-between">
        <span className="text-[7.5px] font-bold tracking-[0.2em] text-[var(--r2-text2)]">LAST TRIGGER · GATE CHAIN</span>
        <button onClick={() => setHist(true)} className="flex items-center gap-1 rounded-sm border border-[var(--r2-border)] px-1.5 py-[1px] text-[7px] font-bold tracking-[0.14em] text-[var(--r2-text2)] hover:text-[var(--r2-text1)]" data-testid="gates-history-open"><History size={8} /> HISTORY {rows.length}</button>
      </div>
      {!last && <div className="text-[var(--r2-text2)]" data-testid="gates-empty">no signal has cleared the confluence bar yet — nothing to veto</div>}
      {last && (<>
        <Head t={last} testid="gates-last-head" />
        <div className="mt-[2px]"><Chain t={last} compact prefix="gates-last" /></div>
      </>)}
      {hist && <HistoryModal rows={histRows ?? rows} symbol={histSym} onSymbol={setHistSym} onClose={() => { setHist(false); setHistSym(symbol); }} />}
    </div>
  );
};
